import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BasketState, BasketStateModel } from '@app/store/state/basket.state';
import {
  SubmitBasketMultiPay,
  SaveExtraCreditCard,
  RemoveGiftCard,
  ValidateBasket,
} from '@app/store/actions/basket.action';
import { BillingAccount } from 'src/app/models/billingaccount.interface';
import { UntypedFormGroup } from '@angular/forms';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Basket } from '@lib/models/olo.basket';
import { GlobalStateModel } from '@app/store/state.model';
import { Capacitor } from '@capacitor/core';
import { OloPaySDK } from '@olo/pay-capacitor';
import { BasketService } from '@app/providers/basket.service';

@Injectable({
  providedIn: 'root',
})
export class SplitPaymentService {
  // For tracking gift card info (manual entry)
  private giftCardInfo: { number: string; pin: string } | null = null;
  private giftCardAmount: number = 0;

  // For tracking gift card payment session (Olo Pay)
  private giftCardSessionInfo: {
    id: string;
    schemeId: string;
    amount: number;
  } | null = null;

  constructor(
    private store: Store,
    private basket: BasketService,
  ) {}

  /**
   * Main entry point for all split payment scenarios
   * Handles both traditional and Olo Pay payment methods
   */
  multiplePayment(
    giftTotal: number,
    ccForm: UntypedFormGroup | null,
    cashAmount: number,
    prepaidAmount: number,
    isKiosk: boolean,
    tableNumber: string,
    isApplePay: boolean = false,
    isGooglePay: boolean = false,
    oloPaySessionInfo?: { paymentSessionId: string; billingSchemeId: string },
  ): Observable<any> {
    let billingInfo: BillingAccount[] = [];
    const basket: BasketStateModel = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);

    // Get billing schemes
    const giftCardScheme = this.getGiftCardBillingScheme(basket);
    const creditCardScheme = basket.billingSchemes.find((sche: any) => sche.type === 'creditcard');

    if (giftTotal > 0 && !giftCardScheme) {
      return throwError(() => new Error('Gift card billing scheme not found'));
    }

    const bilingschemegift: number = giftCardScheme?.id;
    const bilingschemecredit: number = creditCardScheme?.id;

    const basketTotal: number = basket.basketValidation.total;
    const totalnotip: number = basket.basketValidation.total - basket.basket.tip;
    const tip: number = basket.basket.tip || 0;

    // Handle Apple Pay / Google Pay
    if (isApplePay || isGooglePay) {
      return this.handleDigitalWalletPayment(basket, bilingschemegift, giftTotal, tableNumber, isApplePay);
    }

    // Determine payment scenario and create billing accounts
    if (basket.basket.tip) {
      // Account for tip in Multipay
      if (totalnotip > giftTotal) {
        // CC/Cash handles tip
        billingInfo = this.createBillingAccountsWithTip(
          basket,
          bilingschemegift,
          bilingschemecredit,
          ccForm,
          giftTotal,
          cashAmount,
          prepaidAmount,
          oloPaySessionInfo,
          'credit_handles_tip',
        );
      } else {
        // Tip split between payments
        billingInfo = this.createBillingAccountsWithTip(
          basket,
          bilingschemegift,
          bilingschemecredit,
          ccForm,
          giftTotal,
          cashAmount,
          prepaidAmount,
          oloPaySessionInfo,
          'split_tip',
        );
      }
    } else {
      // Multipay without tip
      billingInfo = this.createBillingAccountsNoTip(
        basket,
        bilingschemegift,
        bilingschemecredit,
        basketTotal,
        ccForm,
        giftTotal,
        cashAmount,
        prepaidAmount,
        oloPaySessionInfo,
      );
    }

    // Save credit card info if needed
    if (billingInfo && ccForm) {
      const cc = billingInfo.find((sche: any) => sche.billingmethod === 'creditcard');
      if (cc) {
        const month = cc.expirymonth > 9 ? cc.expirymonth : '0' + cc.expirymonth;
        const year = cc.expiryyear.toString().substr(2, 2);
        const card = {
          cardnumber: cc.cardnumber,
          cvv: cc.cvv,
          expiry: month + year,
          zip: cc.zip,
        };
        this.store.dispatch(new SaveExtraCreditCard(card));
      }
    }

    if (oloPaySessionInfo) {
      return this.basket
        .getStatusOfPaymentSession(
          basket.basket.id,
          oloPaySessionInfo.billingSchemeId,
          oloPaySessionInfo.paymentSessionId,
        )
        .pipe(
          switchMap((status) => {
            if (status.receiptdata?.Customer) {
              const cust = status.receiptdata?.Customer ?? [];
              const get = (k: string) => cust.find((i) => i.Key === k)?.Value || '';

              const cardNumber = get('pan') || 'XXXXXXXXXXXX';
              const cardType = get('paymentMethod') || 'Credit';
              const authCode = get('authCode') || 'AUTH';
              const txRef = get('txRef') || status.id;

              return this.store
                .dispatch(
                  new SubmitBasketMultiPay(billingInfo, cardNumber, cardType, authCode, txRef, null, tableNumber),
                )
                .pipe(
                  catchError((err) => {
                    return this.basket
                      .cancelAPaymentSession(
                        basket.basket.id,
                        oloPaySessionInfo.billingSchemeId,
                        oloPaySessionInfo.paymentSessionId,
                      )
                      .pipe(
                        switchMap(() => {
                          return throwError(err);
                        }),
                      );
                  }),
                );
            }
          }),
        );
    }

    return this.store.dispatch(new SubmitBasketMultiPay(billingInfo, null, null, null, null, null, tableNumber));
  }

  /**
   * Calculates how to distribute tip across multiple payment methods
   */
  private calculateTipPortions(basketTotal: number, tipAmount: number, paymentAmounts: number[]): number[] {
    if (tipAmount === 0) {
      return paymentAmounts.map(() => 0);
    }

    const subtotal = basketTotal - tipAmount;
    const tipPortions: number[] = [];
    let runningTotal = 0;
    let remainingTip = tipAmount;

    for (let i = 0; i < paymentAmounts.length; i++) {
      const paymentAmount = paymentAmounts[i];

      if (i === paymentAmounts.length - 1) {
        // Last payment gets any remaining tip
        tipPortions.push(Number(remainingTip.toFixed(2)));
      } else if (runningTotal < subtotal && runningTotal + paymentAmount > subtotal) {
        // This payment crosses the subtotal boundary
        const tipPortion = Math.min(remainingTip, runningTotal + paymentAmount - subtotal);
        tipPortions.push(Number(tipPortion.toFixed(2)));
        remainingTip -= tipPortion;
      } else if (runningTotal >= subtotal) {
        // We've already covered the subtotal
        const tipPortion = Math.min(remainingTip, paymentAmount);
        tipPortions.push(Number(tipPortion.toFixed(2)));
        remainingTip -= tipPortion;
      } else {
        // This payment only covers product, no tip
        tipPortions.push(0);
      }

      runningTotal += paymentAmount;
    }

    return tipPortions;
  }

  /**
   * Creates billing accounts with tip distribution
   */
  private createBillingAccountsWithTip(
    basket: BasketStateModel,
    bilingschemegift: number,
    bilingschemecredit: number,
    ccForm: UntypedFormGroup | null,
    giftTotal: number,
    cashAmount: number,
    prepaidAmount: number,
    oloPaySessionInfo?: { paymentSessionId: string; billingSchemeId: string },
    tipStrategy: 'credit_handles_tip' | 'split_tip' = 'credit_handles_tip',
  ): BillingAccount[] {
    const billingInfo: BillingAccount[] = [];
    const basketTotal = basket.basketValidation.total;
    const tip = basket.basket.tip;
    const totalnotip = basketTotal - tip;

    if (tipStrategy === 'credit_handles_tip') {
      // Gift cards don't handle tip - use proper amount calculation
      if (basket.giftCard?.length > 0) {
        const giftCardAmounts = this.calculateGiftCardAmounts(basket.giftCard, basketTotal, giftTotal);

        giftCardAmounts.forEach(({ card, amount }) => {
          billingInfo.push({
            billingmethod: 'storedvalue',
            amount: amount,
            tipportion: 0,
            billingschemeid: bilingschemegift,
            billingfields: [
              { name: 'number', value: card.cardnumber },
              { name: 'pin', value: card.pin },
            ],
          });
        });
      }

      // Calculate actual gift amount used for remaining calculation
      const actualGiftAmountUsed = billingInfo
        .filter((b) => b.billingmethod === 'storedvalue')
        .reduce((sum, b) => sum + b.amount, 0);

      // Credit card/cash handles remaining + tip
      const remaining = basketTotal - actualGiftAmountUsed;

      if (cashAmount > 0) {
        billingInfo.push({
          billingmethod: 'cash',
          amount: Number(cashAmount.toFixed(2)),
          tipportion: 0, // Cash typically doesn't get tip
        });
      }

      if (oloPaySessionInfo) {
        // Olo Pay payment session
        const creditAmount = remaining - cashAmount;
        if (creditAmount > 0) {
          billingInfo.push({
            billingmethod: 'paymentsession',
            amount: Number(creditAmount.toFixed(2)),
            tipportion: Number(tip.toFixed(2)),
            paymentsessionid: oloPaySessionInfo.paymentSessionId,
            billingschemeid: Number(oloPaySessionInfo.billingSchemeId),
          });
        }
      } else if (ccForm && ccForm.value.cardNumber) {
        // Traditional credit card
        const creditAmount = remaining - cashAmount;
        if (creditAmount > 0) {
          billingInfo.push({
            billingmethod: 'creditcard',
            amount: Number(creditAmount.toFixed(2)),
            tipportion: Number(tip.toFixed(2)),
            cardnumber: ccForm.value.cardNumber,
            expiryyear: Number('20' + ccForm.value.expDate.substring(2, 4)),
            expirymonth: Number(ccForm.value.expDate.substring(0, 2)),
            cvv: ccForm.value.cvvCode,
            zip: ccForm.value.zipCode,
            billingschemeid: bilingschemecredit,
          });
        }
      }
    } else {
      // Split tip between payments
      return this.gcSplitcc(
        basket,
        bilingschemegift,
        bilingschemecredit,
        basketTotal,
        totalnotip,
        ccForm,
        giftTotal,
        oloPaySessionInfo,
      );
    }

    return billingInfo;
  }

  /**
   * Creates billing accounts without tip
   */
  private createBillingAccountsNoTip(
    basket: BasketStateModel,
    bilingschemegift: number,
    bilingschemecredit: number,
    basketTotal: number,
    ccForm: UntypedFormGroup | null,
    giftTotal: number,
    cashAmount: number,
    prepaidAmount: number,
    oloPaySessionInfo?: { paymentSessionId: string; billingSchemeId: string },
  ): BillingAccount[] {
    const billingInfo: BillingAccount[] = [];

    // Add gift cards with proper amount calculation
    if (basket.giftCard?.length > 0) {
      const giftCardAmounts = this.calculateGiftCardAmounts(basket.giftCard, basketTotal, giftTotal);

      giftCardAmounts.forEach(({ card, amount }) => {
        billingInfo.push({
          billingmethod: 'storedvalue',
          amount: amount,
          tipportion: 0,
          billingschemeid: bilingschemegift,
          billingfields: [
            { name: 'number', value: card.cardnumber },
            { name: 'pin', value: card.pin },
          ],
        });
      });
    }

    // Add cash if present
    if (cashAmount > 0) {
      billingInfo.push({
        billingmethod: 'cash',
        amount: Number(cashAmount.toFixed(2)),
        tipportion: 0,
      });
    }

    // Add prepaid if present
    if (prepaidAmount > 0) {
      billingInfo.push({
        billingmethod: 'prepaid',
        amount: Number(prepaidAmount.toFixed(2)),
        tipportion: 0,
      });
    }

    // Calculate remaining amount for credit card
    const actualGiftAmountUsed = billingInfo
      .filter((b) => b.billingmethod === 'storedvalue')
      .reduce((sum, b) => sum + b.amount, 0);

    const remaining = basketTotal - actualGiftAmountUsed - cashAmount - prepaidAmount;

    // Add credit card if needed
    if (remaining > 0) {
      if (oloPaySessionInfo) {
        // Olo Pay payment session
        billingInfo.push({
          billingmethod: 'paymentsession',
          amount: Number(remaining.toFixed(2)),
          tipportion: 0,
          paymentsessionid: oloPaySessionInfo.paymentSessionId,
          billingschemeid: Number(oloPaySessionInfo.billingSchemeId),
        });
      } else if (ccForm && ccForm.value.cardNumber) {
        // Traditional credit card
        billingInfo.push({
          billingmethod: 'creditcard',
          amount: Number(remaining.toFixed(2)),
          tipportion: 0,
          cardnumber: ccForm.value.cardNumber,
          expiryyear: Number('20' + ccForm.value.expDate.substring(2, 4)),
          expirymonth: Number(ccForm.value.expDate.substring(0, 2)),
          cvv: ccForm.value.cvvCode,
          zip: ccForm.value.zipCode,
          billingschemeid: bilingschemecredit,
        });
      }
    }

    return billingInfo;
  }

  // Split payment -- tip split between Items (complex case)
  private gcSplitcc(
    basket: BasketStateModel,
    bilingschemegift: number,
    bilingschemecredit: number,
    basketTotal: number,
    totalnotip: number,
    ccForm: UntypedFormGroup | null,
    giftTotal: number,
    oloPaySessionInfo?: { paymentSessionId: string; billingSchemeId: string },
  ): BillingAccount[] {
    const billingInfo: BillingAccount[] = [];
    const amounts: number[] = [];

    // Collect all payment amounts
    basket.giftCard.forEach((card: any) => amounts.push(card.balance));
    if (basketTotal > giftTotal) {
      amounts.push(basketTotal - giftTotal);
    }

    // Calculate tip portions
    const tipPortions = this.calculateTipPortions(basketTotal, basket.basket.tip, amounts);

    // Create billing accounts with calculated tips - use proper amount calculation
    const giftCardAmounts = this.calculateGiftCardAmounts(basket.giftCard, basketTotal, giftTotal);

    giftCardAmounts.forEach(({ card, amount }, i) => {
      billingInfo.push({
        billingmethod: 'storedvalue',
        amount: amount,
        tipportion: tipPortions[i],
        billingschemeid: bilingschemegift,
        billingfields: [
          { name: 'number', value: card.cardnumber },
          { name: 'pin', value: card.pin },
        ],
      });
    });

    // Calculate actual remaining amount for credit card
    const actualGiftAmountUsed = billingInfo
      .filter((b) => b.billingmethod === 'storedvalue')
      .reduce((sum, b) => sum + b.amount, 0);

    const remaining = basketTotal - actualGiftAmountUsed;

    // Add credit card if needed
    if (remaining > 0) {
      const ccTipPortion = tipPortions[tipPortions.length - 1];

      if (oloPaySessionInfo) {
        billingInfo.push({
          billingmethod: 'paymentsession',
          amount: Number(remaining.toFixed(2)),
          tipportion: ccTipPortion,
          paymentsessionid: oloPaySessionInfo.paymentSessionId,
          billingschemeid: Number(oloPaySessionInfo.billingSchemeId),
        });
      } else if (ccForm && ccForm.value.cardNumber) {
        billingInfo.push({
          billingmethod: 'creditcard',
          amount: Number(remaining.toFixed(2)),
          tipportion: ccTipPortion,
          cardnumber: ccForm.value.cardNumber,
          expiryyear: Number('20' + ccForm.value.expDate.substring(2, 4)),
          expirymonth: Number(ccForm.value.expDate.substring(0, 2)),
          cvv: ccForm.value.cvvCode,
          zip: ccForm.value.zipCode,
          billingschemeid: bilingschemecredit,
        });
      }
    }

    return billingInfo;
  }

  /**
   * Handles digital wallet payments (Apple Pay / Google Pay)
   */
  private handleDigitalWalletPayment(
    basket: BasketStateModel,
    bilingschemegift: number,
    giftTotal: number,
    tableNumber: string,
    isApplePay: boolean,
  ): Observable<any> {
    const applePayScheme = basket.billingSchemes.find(
      (scheme: any) => scheme.type === 'external' && scheme.name === 'Apple Pay',
    )?.id;
    const googlePayScheme = basket.billingSchemes.find(
      (scheme: any) => scheme.type === 'external' && scheme.name === 'Google Pay',
    )?.id;

    return this.store.dispatch(new ValidateBasket()).pipe(
      switchMap(() => {
        const billingInfo: BillingAccount[] = [];

        // Add gift cards
        basket.giftCard?.forEach((card: any) => {
          billingInfo.push({
            billingmethod: 'storedvalue',
            amount: card.balance,
            tipportion: 0,
            billingschemeid: bilingschemegift,
            billingfields: [
              { name: 'number', value: card.cardnumber },
              { name: 'pin', value: card.pin },
            ],
          });
        });

        return this.store
          .selectOnce((state) => state.basket.basket)
          .pipe(
            switchMap((currentBasket: Basket) => {
              return from(
                OloPaySDK.getDigitalWalletPaymentMethod({
                  currencyCode: 'USD',
                  countryCode: 'US',
                  amount: currentBasket.total - giftTotal,
                }),
              ).pipe(
                switchMap((res) => {
                  billingInfo.push({
                    amount: currentBasket.total - giftTotal,
                    tipportion: currentBasket.tip,
                    token: res.paymentMethod.id,
                    cardtype: res.paymentMethod.cardType,
                    cardlastfour: res.paymentMethod.last4,
                    billingmethod: 'digitalwallet',
                    billingschemeid: isApplePay ? applePayScheme : googlePayScheme,
                  });

                  return this.store.dispatch(
                    new SubmitBasketMultiPay(billingInfo, null, null, null, null, null, tableNumber),
                  );
                }),
              );
            }),
          );
      }),
    );
  }

  /**
   * Helper method to calculate gift card amounts properly
   * Ensures that gift cards don't exceed the basket total or available balances
   */
  private calculateGiftCardAmounts(
    giftCards: any[],
    basketTotal: number,
    giftTotal: number,
  ): { card: any; amount: number }[] {
    const result: { card: any; amount: number }[] = [];
    let remainingBasketAmount = basketTotal;
    let remainingGiftTotal = giftTotal;

    giftCards.forEach((card: any) => {
      // Calculate how much of this gift card should be used
      const cardAmountToUse = Math.min(card.balance, remainingBasketAmount, remainingGiftTotal);

      if (cardAmountToUse > 0) {
        result.push({
          card: card,
          amount: Number(cardAmountToUse.toFixed(2)),
        });

        // Update remaining amounts
        remainingBasketAmount -= cardAmountToUse;
        remainingGiftTotal -= cardAmountToUse;
      }
    });

    // Debug logging for troubleshooting
    console.debug('Gift Card Amount Calculation:', {
      originalBasketTotal: basketTotal,
      originalGiftTotal: giftTotal,
      calculatedAmounts: result.map((r) => ({ cardNumber: r.card.cardnumber, amount: r.amount })),
      totalGiftAmountUsed: result.reduce((sum, r) => sum + r.amount, 0),
    });

    return result;
  }

  /**
   * Helper to get gift card billing scheme
   */
  private getGiftCardBillingScheme(basket: BasketStateModel): any {
    const schemes = basket.billingSchemes?.filter(
      (sche: any) => sche.type?.toLowerCase() === 'giftcard' || sche.type?.toLowerCase() === 'storedvalue',
    );

    return schemes && schemes.length > 0 ? schemes[0] : null;
  }

  /**
   * Sets gift card information for manual entry
   * @param info The gift card number and pin
   */
  setGiftCardInfo(info: { number: string; pin: string }): void {
    this.giftCardInfo = info;
  }

  /**
   * Gets the stored gift card information
   * @returns The gift card number and pin, or null if not set
   */
  getGiftCardInfo(): { number: string; pin: string } | null {
    return this.giftCardInfo;
  }

  /**
   * Sets the gift card amount that will be applied to the order
   * @param amount The amount from the gift card
   */
  setGiftCardAmount(amount: number): void {
    this.giftCardAmount = amount;
  }

  /**
   * Gets the stored gift card amount
   * @returns The gift card amount
   */
  getGiftCardAmount(): number {
    return this.giftCardAmount;
  }

  /**
   * Sets gift card payment session information for Olo Pay
   * @param sessionInfo The payment session ID, scheme ID, and amount
   */
  setGiftCardSessionInfo(sessionInfo: { id: string; schemeId: string; amount: number }): void {
    this.giftCardSessionInfo = sessionInfo;
  }

  /**
   * Gets the stored gift card payment session information
   * @returns The gift card payment session info, or null if not set
   */
  getGiftCardSessionInfo(): { id: string; schemeId: string; amount: number } | null {
    return this.giftCardSessionInfo;
  }

  /**
   * Clears all gift card information
   */
  clearGiftCardInfo(): void {
    this.giftCardInfo = null;
    this.giftCardAmount = 0;
    this.giftCardSessionInfo = null;
  }
}
